import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import {
  faExclamationTriangle,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useRouting } from 'src/routes'
import { RelativeLink } from 'src/system/ui/link/RelativeLink'
import {
  brighten,
  hoverAmount,
  hoverTransition
} from 'src/system/ui/theme/hover'
import type { UserFieldsFragment as User } from 'src/graphql/types'

// The dismissal is intentionally temporary — we never persist that the user
// permanently hid this. They can close it for a while, but it comes back so a
// still-misconfigured account keeps getting nudged.
const dismissKey = 'valveAutoDownloadWarningDismissedAt'
const dismissDurationMs = 24 * 60 * 60 * 1000

function isDismissed(): boolean {
  try {
    const raw = globalThis.localStorage.getItem(dismissKey)
    if (!raw) return false
    const dismissedAt = Number(raw)
    if (!Number.isFinite(dismissedAt)) return false
    // Reject future timestamps (corruption / clock skew) so a bad value can't
    // keep the warning hidden past the intended window.
    const elapsed = Date.now() - dismissedAt
    return elapsed >= 0 && elapsed < dismissDurationMs
  } catch {
    return false
  }
}

// A steam-linked user whose automatch is inactive (no Steam game auth code) is
// not getting their Valve / CS2 official matches auto-downloaded — surface the
// warning. This is the same edge case the route gate lets through when the user
// is also FACEIT-linked.
export function shouldShowValveAutoDownloadWarning(
  user: User | null | undefined
): boolean {
  return Boolean(user?.isAccountLinked && !user.automatch.isActive)
}

export function ValveAutoDownloadWarning() {
  const { t } = useTranslation()
  const { routes } = useRouting()
  const [dismissed, setDismissed] = useState(isDismissed)

  if (dismissed) return null

  const dismiss = () => {
    try {
      globalThis.localStorage.setItem(dismissKey, String(Date.now()))
    } catch {
      // localStorage unavailable (e.g. blocked): hide it for this view. Without
      // persistence it will reappear on a full remount, which is acceptable.
    }
    setDismissed(true)
  }

  return (
    <Box>
      <Header>
        <Title>
          <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
          {t('matches:valve_auto_download_warning:title')}
        </Title>
        <CloseButton
          type='button'
          onClick={dismiss}
          aria-label={t('matches:valve_auto_download_warning:dismiss')}
        >
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </Header>
      <Description>
        <Trans i18nKey='matches:valve_auto_download_warning:body'>
          {' '}
          <RelativeLink to={routes.account}> </RelativeLink>
        </Trans>
      </Description>
    </Box>
  )
}

const Box = styled.div`
  margin-top: ${(props) => props.theme.d.spacing[2]};
  margin-bottom: ${(props) => props.theme.d.spacing[4]};
  padding: ${(props) =>
    `${props.theme.d.spacing[2]} ${props.theme.d.spacing[4]}`};
  border: 1px solid ${(props) => props.theme.d.colors.warning};
  border-radius: 4px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(props) => props.theme.d.spacing[2]};
`

const Title = styled.h3`
  color: ${(props) => props.theme.d.colors.warning};
  font-weight: bold;
  font-size: 14px;
  margin: 0;
`

const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  line-height: 1;
  font-size: 14px;
  cursor: pointer;
  color: ${(props) => props.theme.d.colors.grey};
  transition: color ${hoverTransition};

  &:hover {
    color: ${(props) => brighten(props.theme.d.colors.grey, hoverAmount.text)};
  }
`

const Description = styled.p`
  margin: ${(props) => `${props.theme.d.spacing[2]} 0 0`};
  font-size: 13px;
`
